import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import localeData from "dayjs/plugin/localeData";
import isBetween from "dayjs/plugin/isBetween";

dayjs.extend(customParseFormat);
dayjs.extend(localeData);
dayjs.extend(isBetween);

/** *****************************************************************************
 * Loads locale info for date picker
 * @param {string} locale
 */
export const loadLocale = (locale) => {
  if (locale && dayjs.locale() !== locale) {
    // eslint-disable-next-line
    require(`dayjs/locale/${locale}.js`);
    dayjs.locale(locale);
  }
};

/** *****************************************************************************
 * Returns the dayjs object
 * @param {(Date|string)} dt
 * @param {Array<string> | string} dtFormat
 */
export const parseToDayJs = (dt, dtFormat, locale) => {
  loadLocale(locale);
  return typeof dt === "string" ? dayjs(dt, dtFormat, locale) : dayjs(dt);
};

/** *****************************************************************************
 * Returns a new dayjs object by changing its months
 * @param {dayjs} date
 * @param {number} nav +1 (add) or -1(subtract)
 * @param {number} by how many days to add or subtract?
 */
export const changeDayBy = (date, nav, noOfDays) =>
  nav > 0
    ? date.clone().add(noOfDays, "day")
    : date.clone().subtract(noOfDays, "day");

/** *****************************************************************************
 * Returns a new dayjs object by changing its months
 * @param {dayjs} date
 * @param {number} nav +1 (add) or -1(subtract)
 * @param {number} by how many months to add or subtract?
 */
export const changeMonthBy = (date, nav, noOfMonths) =>
  nav > 0
    ? date.clone().add(noOfMonths, "month")
    : date.clone().subtract(noOfMonths, "month");

/** *****************************************************************************
 * Returns a new dayjs object by changing its year
 * @param {dayjs} date
 * @param {number} nav +1 (add) or -1(subtract)
 * @param {number} by how many years to add or subtract?
 */
export const changeYearBy = (date, nav, onOfYears) =>
  nav > 0
    ? date.clone().add(onOfYears, "year")
    : date.clone().subtract(onOfYears, "year");

/** *****************************************************************************
 * Returns list of min week names with respective to the first day of the week
 * @param {string} locale
 * @param {number} firstDayOfTheWeek
 *
 * Eg: getWeekDays('en', 0) // returns [SU, MO, TU, WE, TH, FR, SA]
 * Eg: getWeekDays('en', 1) // returns [MO, TU, WE, TH, FR, SA, SU]
 */
export const getWeekDays = (locale, firstDayOfTheWeek) => {
  loadLocale(locale);
  const shortWeekNames = Object.assign([], dayjs.weekdaysMin());
  const fullWeekNames = Object.assign([], dayjs.weekdays());
  const append = shortWeekNames.map((shortName, i) => ({
    abbr: shortName,
    full: fullWeekNames[i],
  }));
  const prepend = append.splice(firstDayOfTheWeek);
  return prepend.concat(append);
};

/** *****************************************************************************
 * Returns names of the months for given locale
 * @param {string} locale
 */
export const getMonths = (locale) => {
  loadLocale(locale);
  return dayjs.months();
};

/** *****************************************************************************
 * Returns dayjs object of today's date
 */
export const getToday = () =>
  dayjs().hour(0).minute(0).second(0).millisecond(0);

/** *****************************************************************************
 * Returns true or false depends on past/future dates based on given min and max
 * dates
 * @param {dayjs} dayObj
 * @param {dayjs} dayObj
 * @param {dayjs} dayObj
 *
 * @returns {boolean}
 */
export const isDisabledDate = (dayObj, minDate, maxDate) => {
  let isDisabled = false;

  // disable dates beyond minDate when maxDate is invalid
  if (minDate?.isValid() && !maxDate?.isValid()) {
    isDisabled = dayObj.isBefore(minDate, "date");
  }

  // disable dates beyond maxDate when minDate is invalid
  if (maxDate?.isValid() && !minDate?.isValid()) {
    isDisabled = dayObj.isAfter(maxDate, "date");
  }

  // disable dates between valid and invalid maxDates
  if (maxDate?.isValid() && minDate?.isValid()) {
    isDisabled =
      dayObj.isBefore(minDate, "date") || dayObj.isAfter(maxDate, "date");
  }

  return isDisabled;
};
